import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Save, Loader } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../contexts/ToastContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminExamConfigModal({ isOpen, onClose, courseId, authHeaders, lang = 'tr' }) {
  const toast = useToast();
  const [enabled, setEnabled] = useState(true);
  const [passingScore, setPassingScore] = useState(70);
  const [timeLimit, setTimeLimit] = useState(30);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const tr = (t, e) => lang === 'tr' ? t : e;

  useEffect(() => {
    if (!isOpen || !courseId) return;
    (async () => {
      setLoading(true);
      setError('');
      try {
        // GET /courses/{id} response'u: {course, lessons, reviews}
        const res = await axios.get(`${API}/courses/${courseId}`, { headers: authHeaders() });
        const fe = res.data.course?.final_exam;
        if (fe) {
          setEnabled(fe.enabled ?? true);
          setPassingScore(fe.passing_score ?? 70);
          setTimeLimit(fe.time_limit_minutes ?? 30);
          setMaxAttempts(fe.max_attempts ?? 3);
          setQuestions(fe.questions || []);
        } else {
          setEnabled(false);
          setPassingScore(70);
          setTimeLimit(30);
          setMaxAttempts(3);
          setQuestions([]);
        }
      } catch {
        setError(tr('Yüklenemedi', 'Could not load'));
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, courseId]); // eslint-disable-line

  const addQuestion = () => {
    setQuestions(qs => [...qs, {
      question: '',
      type: 'multiple_choice',
      options: ['', '', '', ''],
      correct_answer: '',
      explanation: '',
      points: 10,
    }]);
  };

  const updateQuestion = (i, field, value) => {
    setQuestions(qs => qs.map((q, idx) => idx === i ? { ...q, [field]: value } : q));
  };

  const updateOption = (qi, oi, value) => {
    setQuestions(qs => qs.map((q, idx) => {
      if (idx !== qi) return q;
      const newOpts = [...(q.options || [])];
      newOpts[oi] = value;
      return { ...q, options: newOpts };
    }));
  };

  const removeQuestion = (i) => {
    if (!window.confirm(tr('Bu soruyu silmek istediğinize emin misiniz?', 'Delete this question?'))) return;
    setQuestions(qs => qs.filter((_, idx) => idx !== i));
  };

  const handleSave = async () => {
    if (enabled && questions.length === 0) {
      setError(tr('En az bir soru eklemelisiniz', 'Add at least one question'));
      return;
    }
    for (const [i, q] of questions.entries()) {
      if (!q.question || !q.correct_answer) {
        setError(tr(`Soru ${i + 1}: eksik alan (soru metni veya doğru cevap)`, `Question ${i + 1}: missing fields`));
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      await axios.put(
        `${API}/admin/courses/${courseId}/final-exam`,
        { enabled, passing_score: passingScore, time_limit_minutes: timeLimit, max_attempts: maxAttempts, questions },
        { headers: authHeaders() }
      );
      toast.success(tr('Sınav kaydedildi', 'Exam saved'));
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || tr('Kayıt başarısız', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
            className="relative bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="font-bold text-lg">{tr('Final Sınavı Ayarları', 'Final Exam Settings')}</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {loading && <div className="text-center py-8 text-gray-500">{tr('Yükleniyor...', 'Loading...')}</div>}
              {!loading && (
                <>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={e => setEnabled(e.target.checked)}
                      data-testid="exam-enabled-toggle"
                    />
                    <span className="font-bold">{tr('Sınav Aktif', 'Exam Enabled')}</span>
                  </label>

                  <div className="grid grid-cols-3 gap-3">
                    <NumField label={tr('Gecer Not (%)', 'Passing Score')} value={passingScore} setValue={setPassingScore} min={0} max={100} />
                    <NumField label={tr('Sure (dk, 0=sinirsiz)', 'Time (min, 0=unlimited)')} value={timeLimit} setValue={setTimeLimit} min={0} />
                    <NumField label={tr('Deneme (0=sinirsiz)', 'Attempts (0=unlimited)')} value={maxAttempts} setValue={setMaxAttempts} min={0} />
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold">{tr('Sorular', 'Questions')} ({questions.length})</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={addQuestion}
                          className="px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-bold flex items-center gap-1 hover:bg-green-600"
                          data-testid="add-question-btn"
                        >
                          <Plus size={14} /> {tr('Soru Ekle', 'Add Question')}
                        </button>
                      </div>
                    </div>

                    {questions.length === 0 && (
                      <div className="text-center text-gray-400 py-6 text-sm">{tr('Henuz soru yok. Yukaridan ekleyin.', 'No questions yet.')}</div>
                    )}

                    {questions.map((q, i) => (
                      <div key={i} className="border-2 border-gray-200 rounded-xl p-4 mb-3" data-testid={`question-editor-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold text-gray-500">{tr('Soru', 'Q')} {i + 1}</span>
                          <button
                            onClick={() => removeQuestion(i)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                            data-testid={`remove-question-${i}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={q.question}
                          onChange={e => updateQuestion(i, 'question', e.target.value)}
                          placeholder={tr('Soru metni', 'Question text')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 text-sm"
                          data-testid={`question-text-${i}`}
                        />
                        <div className="flex gap-2 mb-2">
                          <select
                            value={q.type}
                            onChange={e => updateQuestion(i, 'type', e.target.value)}
                            className="px-2 py-1.5 border border-gray-300 rounded text-xs"
                          >
                            <option value="multiple_choice">{tr('Çoktan Seçmeli', 'Multiple Choice')}</option>
                            <option value="true_false">{tr('Doğru / Yanlış', 'True / False')}</option>
                          </select>
                          <input
                            type="number"
                            value={q.points}
                            onChange={e => updateQuestion(i, 'points', parseInt(e.target.value) || 0)}
                            className="w-20 px-2 py-1.5 border border-gray-300 rounded text-xs"
                            placeholder={tr('Puan', 'Points')}
                          />
                        </div>

                        {q.type === 'multiple_choice' && (
                          <div className="space-y-1">
                            {(q.options || ['', '', '', '']).map((opt, oi) => (
                              <div key={oi} className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={`correct-${i}`}
                                  checked={q.correct_answer === opt && opt !== ''}
                                  onChange={() => updateQuestion(i, 'correct_answer', opt)}
                                  disabled={!opt}
                                />
                                <input
                                  type="text"
                                  value={opt}
                                  onChange={e => updateOption(i, oi, e.target.value)}
                                  placeholder={`${tr('Seçenek', 'Option')} ${oi + 1}`}
                                  className="flex-1 px-2 py-1 border border-gray-200 rounded text-sm"
                                />
                              </div>
                            ))}
                            <p className="text-xs text-gray-500 mt-1">{tr('Doğru cevabı solundaki radyo ile işaretleyin', 'Mark correct with radio')}</p>
                          </div>
                        )}

                        {q.type === 'true_false' && (
                          <div className="flex gap-3 mt-2">
                            {['Dogru', 'Yanlis'].map(val => (
                              <label key={val} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`tf-${i}`}
                                  checked={q.correct_answer === val}
                                  onChange={() => {
                                    updateQuestion(i, 'options', ['Dogru', 'Yanlis']);
                                    updateQuestion(i, 'correct_answer', val);
                                  }}
                                />
                                {val}
                              </label>
                            ))}
                          </div>
                        )}

                        <input
                          type="text"
                          value={q.explanation || ''}
                          onChange={e => updateQuestion(i, 'explanation', e.target.value)}
                          placeholder={tr('Açıklama (opsiyonel)', 'Explanation (optional)')}
                          className="w-full px-3 py-1.5 border border-gray-200 rounded text-xs mt-2"
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
            </div>

            <div className="p-5 border-t bg-gray-50 flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 rounded-lg hover:bg-gray-200 text-sm">{tr('İptal', 'Cancel')}</button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 rounded-lg bg-blue-500 text-white font-bold text-sm hover:bg-blue-600 flex items-center gap-2 disabled:opacity-50"
                data-testid="save-exam-btn"
              >
                {saving && <Loader size={16} className="animate-spin" />}
                <Save size={16} />
                {tr('Kaydet', 'Save')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function NumField({ label, value, setValue, min = 0, max = null }) {
  return (
    <div>
      <label className="block text-xs font-bold mb-1">{label}</label>
      <input
        type="number"
        value={value}
        onChange={e => setValue(parseInt(e.target.value) || 0)}
        min={min}
        max={max || undefined}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
      />
    </div>
  );
}
